import React, { useMemo, useCallback } from 'react';
import { Panel, Stack, Box, Inline, Text, Tag } from 'src/components/matchbox';
import { Empty, Loading } from 'src/components';
import { Heading, TranslatableText } from 'src/components/text';
import LineChart from 'src/components/charts/LineChart';
import { useSparkPostQuery } from 'src/hooks';
import { getTimeSeries } from 'src/helpers/api/metrics';
import { transformData } from 'src/helpers/metrics';
import { useAlertForm } from '../useAlertForm';
import { getMetricsFromKeys, toQueryFromOptions } from '@sparkpost/report-builder/helpers/metrics';
import { getRelativeDates, getLineChartFormatters } from '@sparkpost/report-builder/helpers/date';
import METRICS_UNIT_CONFIG from 'src/appConfig/metrics-units';
import { ListBox } from 'src/components/matchbox';
import { TimezoneTypeahead } from 'src/components/typeahead/TimezoneTypeahead';
import styled from 'styled-components';
import { tokens } from '@sparkpost/design-tokens';
import { ActiveFilters } from 'src/components';
import { useAnalyticsReportContext } from 'src/pages/analyticsReport/context/AnalyticsReportContext';
import CustomTooltip from 'src/components/analyticsReport/Tooltip';
import { useRouteMatch } from 'react-router-dom';

const dateTimeReducer = (state, action) => {
  switch (action.type) {
    case 'SET_RANGE':
      const ret = { ...state };
      if (action.value !== 'day') {
        ret.selectedPrecision = 'day';
        ret.selectedTimezone = 'UTC';
      }
      return { ...ret, selectedRange: action.value };
    case 'SET_TIMEZONE':
      return { ...state, selectedTimezone: action.value };
    case 'SET_PRECISION':
      return { ...state, selectedPrecision: action.value };
    default:
      throw new Error(`${action.type} is not a valid action for this reducer`);
  }
};
const initialState = {
  selectedRange: 'day',
  timezone: 'UTC',
  selectedPrecision: 'hour'
};

const StyledQuote = styled(Text).attrs({
  as: 'blockquote'
})`
  border-left: 1px solid #d9e0e6;
  padding-left: ${tokens.spacing_200};
  margin-left: ${tokens.spacing_200};
  margin-top: 0;
  color: ${tokens.color_gray_700};
`;

const LoadingContainer = styled(Box)`
  svg {
    margin-top: ${tokens.spacing_850};
  }
`;

export default function AlertSummary() {
  const {
    state: { metricData, conditionData }
  } = useAlertForm();
  const [state, dispatch] = React.useReducer(dateTimeReducer, initialState);
  const { selectedMetric = 'day' } = metricData;
  const hasSelectedMetric = Boolean(selectedMetric);
  const forcePrecisionDay = state.selectedRange !== 'day';
  const isEditingAlert = useRouteMatch('/alerts/new/edit/:alertId');

  const formattedMetrics = useMemo(() => {
    if (!hasSelectedMetric) {
      return [];
    }
    return getMetricsFromKeys([selectedMetric], true);
  }, [selectedMetric, hasSelectedMetric]);

  const { from, to } = getRelativeDates(state.selectedRange);

  const { state: reportOptions } = useAnalyticsReportContext();

  const query = toQueryFromOptions({
    filters: reportOptions.filters,
    metrics: formattedMetrics,
    from,
    to,
    precision: state.selectedPrecision,
    timezone: state.selectedTimezone
  });

  const { status, data } = useSparkPostQuery(() => getTimeSeries(query), {
    enabled: Boolean(selectedMetric),
    select: (data) => {
      return transformData(data, formattedMetrics);
    }
  });

  const formattedMetric = formattedMetrics[0] || {};
  const unit = formattedMetrics[0]?.unit;
  const chartConfig = METRICS_UNIT_CONFIG[unit];
  const formatters = getLineChartFormatters(state.selectedPrecision, to);
  const line = [
    {
      key: formattedMetric.key,
      stroke: formattedMetric.stroke,
      dataKey: formattedMetric.key,
      name: formattedMetric.label
    }
  ];

  const setRange = useCallback(
    (e) => {
      dispatch({ type: 'SET_RANGE', value: e.currentTarget.value });
    },
    [dispatch]
  );

  const setTimezone = useCallback(
    ({ value }) => {
      dispatch({ type: 'SET_TIMEZONE', value });
    },
    [dispatch]
  );

  const setPrecision = useCallback(
    (e) => {
      dispatch({ type: 'SET_RANGE', value: e.currentTarget.value });
    },
    [dispatch]
  );

  return (
    <Box height="296px">
      {status === 'success' || status === 'loading' ? (
        <Panel>
          <Panel.Section>
            <Stack>
              <Heading as="h4">Alert Summary</Heading>
              {!metricData.selectedMetric && (
                <Text>
                  The alert summary will auto-populate when a metric and configurations are
                  selected.
                </Text>
              )}
              {metricData.selectedMetric && (
                <Box>
                  <TranslatableText>The alert is triggered when </TranslatableText>
                  <Tag color="blue">{metricData.selectedMetric.label}</Tag>
                  <TranslatableText> is </TranslatableText>
                  {conditionData.condition && (
                    <>
                      <Tag color="blue">{conditionData.condition}</Tag>{' '}
                    </>
                  )}

                  {(!conditionData.condition || !conditionData.alertThreshold) && (
                    <TranslatableText>... </TranslatableText>
                  )}

                  {conditionData.alertThreshold && (
                    <Tag color="blue">{conditionData.alertThreshold}%</Tag>
                  )}

                  {conditionData.precision && (
                    <>
                      <TranslatableText> at the end of a </TranslatableText>
                      <Tag color="blue">1 {conditionData.precision}</Tag> period.
                    </>
                  )}
                </Box>
              )}
              {reportOptions.filters.length > 0 && (
                <StyledQuote>
                  <ActiveFilters filters={reportOptions.filters} />
                </StyledQuote>
              )}
            </Stack>
          </Panel.Section>
          <Panel.Section>
            <Stack>
              <Inline>
                <ListBox
                  value={state.selectedRange}
                  onChange={setRange}
                  label="Time Range"
                  id="alert-time-range"
                >
                  <ListBox.Option value="day">Last 24 hours</ListBox.Option>
                  <ListBox.Option value="7days">Last 7 days</ListBox.Option>
                  <ListBox.Option value="30days">Last 30 days</ListBox.Option>
                  <ListBox.Option value="90days">Last 90 days</ListBox.Option>
                </ListBox>
                <TimezoneTypeahead
                  id="alert-time-zone"
                  disabled={forcePrecisionDay}
                  value={state.selectedTimezone}
                  onChange={setTimezone}
                />
                <ListBox
                  id="alert-precision"
                  value={state.selectedPrecision}
                  label="Precision"
                  onChange={setPrecision}
                  disabled={forcePrecisionDay || isEditingAlert}
                >
                  <ListBox.Option value="hour">Hour</ListBox.Option>
                  <ListBox.Option value="day">Day</ListBox.Option>
                </ListBox>
              </Inline>

              {status !== 'loading' ? (
                <LineChart
                  {...formatters}
                  yTickFormatter={chartConfig.yAxisFormatter}
                  showXAxis
                  height={200}
                  unit="percent"
                  data={data || []}
                  lines={line}
                  tooltip={CustomTooltip}
                  tooltipValueFormatter={chartConfig.yAxisFormatter}
                  showTooltip
                />
              ) : (
                <LoadingContainer>
                  <Loading as={Box} minHeight={tokens.spacing_900} />
                </LoadingContainer>
              )}
            </Stack>
          </Panel.Section>
        </Panel>
      ) : (
        <Empty message="No Data Available" description="Select a metric to view graph." />
      )}
    </Box>
  );
}
